import { useState } from 'react';

import {
  type ContactStatus,
  CAM_ENTRY_AT,
  DRAFT_LIST_ID,
  MESSAGE_ALL_AT,
  NEXT_TIMES,
  PAID_TIMES,
  PUBLISHED_AT,
  SECURED_AT,
  shortlistsFor,
  usesPaywall,
  type CareRequest,
  type PaywallProvider,
  type PaywallShortlist,
  type ProviderAnswers,
  type Scenario,
} from '@/cam-admin/paywall/Shortlist.fixtures';

// All the shortlist state in one place, so the table and the row drawer can be
// rendered in two different parts of the control panel while working on the same
// data.
//
// They have to be split like that: the drawer covers the whole workspace, which
// means it belongs in ControlPanel's overlays slot at the shell root, while the
// table belongs in the tab body. Sharing a hook rather than lifting the state
// into ControlPanel keeps the shortlist logic in this folder.

export type ShortlistInitial = {
  scenario?: Scenario;
  published?: boolean;
  /** Which shortlist to open on. Defaults to the draft. */
  shortlistId?: string;
  /**
   * Open on the publish-and-send modal. For the story and the exploration frame
   * that are about the send itself — reaching it by clicking Publish and send
   * would mean every screenshot of it started somewhere else.
   */
  publishOpen?: boolean;
  /**
   * Open on the care home publish confirm. The dom equivalent of `publishOpen`,
   * and separate because they are different modals on different kinds of list.
   */
  homePublishOpen?: boolean;
  /** Open on the preview of what is about to go out. Same reasoning again. */
  previewOpen?: boolean;
  /**
   * Open with a row's drawer showing, by provider id. Same reasoning as
   * publishOpen: the drawer is the subject of its own frame, and a frame that
   * needs a click first is a frame of the table.
   */
  openRow?: string;
};

// Whether the family can see this provider. Paying is the ordinary route: a
// provider who has paid is visible the moment Stripe confirms, with nobody
// deciding anything. `published` is the CAM override on top of that, for the
// provider being let through without paying.
//
// This is why the column says Visible rather than Published. Published belongs to
// the list — one status, on one object — and it moved to meaning "the links have
// gone out", which every row on the list shares and none of them differs on.
export const isVisibleToFamily = (p: PaywallProvider) =>
  p.state === 'paid' || p.published === true;

// What the send goes to. A provider who has already declined is out of the
// running, so a resend to them would be chasing somebody who answered.
export const isAwaitingReply = (p: PaywallProvider) => p.state === 'messaged';

// The draft of the request, while the modal is open. Seeded from the shortlist's
// own care request plus an empty extra: the assessment is the source and this is
// the last read of it before it leaves.
export type SendDraft = CareRequest & {
  careType: string;
  area: string;
  /** Anything else providers should know. CAM's own words, and always optional. */
  extra: string;
};

export const draftFromList = (list: PaywallShortlist): SendDraft => ({
  ...list.careRequest,
  careType: list.careType,
  area: list.location,
  extra: '',
});

export const useShortlists = ({
  scenario = 'fresh',
  published = false,
  shortlistId = DRAFT_LIST_ID,
  publishOpen = false,
  homePublishOpen = false,
  previewOpen = false,
  openRow: initialOpenRow,
}: ShortlistInitial) => {
  const [lists, setLists] = useState(() => shortlistsFor(scenario, published));
  const [selectedId, setSelectedId] = useState(shortlistId);
  // Which row's drawer is open, by provider id.
  const [openRow, setOpenRow] = useState<string | null>(initialOpenRow ?? null);
  // The publish-and-send modal: null when closed, otherwise the draft of what is
  // about to go out. Held here rather than in the dialog so the tab, the dialog
  // and the row drawer are all looking at one shortlist.
  //
  // There is no tick-per-provider set any more. Sending and publishing are one
  // act now, so at the moment the modal is open nobody has paid and there is
  // nothing to hold back — the decision it carries is what we are about to say,
  // not who we are about to say it to.
  const [sendDraft, setSendDraft] = useState<SendDraft | null>(() => {
    if (!publishOpen && !previewOpen) return null;
    const list = shortlistsFor(scenario, published).find(
      (l) => l.id === shortlistId,
    );
    return list ? draftFromList(list) : null;
  });
  // Whether the modal has handed over to the full-screen preview. A mode rather
  // than a second overlay: it is the same draft being read, and a dialog stacked
  // on a dialog would leave a CAM two Cancels deep.
  const [previewing, setPreviewing] = useState(previewOpen);
  // Timestamps are handed out per shortlist so switching lists doesn't make the
  // next click on the other one jump forward in time.
  const [clicks, setClicks] = useState<Record<string, number>>({});
  // The shortlist dialog: null when closed, otherwise which job it is doing.
  // Open state lives here with the other two overlays, because the dialog is
  // rendered at the shell root while the controls that open it are in the tab
  // body.
  const [dialog, setDialog] = useState<'create' | 'edit' | null>(null);
  // The care home publish confirm. A boolean rather than a draft, because there
  // is nothing to draft: the modal carries a sentence, not a message.
  const [confirmingHomePublish, setConfirmingHomePublish] =
    useState(homePublishOpen);

  const current = lists.find((l) => l.id === selectedId) ?? lists[0];
  const providers = current.providers;

  const nextTime = (key: string, pool: readonly string[]) => {
    const used = clicks[key] ?? 0;
    setClicks((prev) => ({ ...prev, [key]: used + 1 }));
    return pool[Math.min(used, pool.length - 1)];
  };

  // Every mutation is scoped to the selected shortlist: two lists can hold the
  // same provider in different states, which is the whole reason to switch.
  const patchProvider = (id: string, patch: Partial<PaywallProvider>) =>
    setLists((prev) =>
      prev.map((l) =>
        l.id !== current.id
          ? l
          : {
              ...l,
              providers: l.providers.map((p) =>
                p.id === id ? { ...p, ...patch } : p,
              ),
            },
      ),
    );

  return {
    lists,
    current,
    providers,
    isPublished: current.status === 'published',
    // Whether this list runs the paywall, which on this tab is the only branch
    // there is. A dom list sends, takes payments and gates who the family sees; a
    // care home list does none of the three, because a CAM rang the homes and
    // there is no lead for anybody to buy.
    isPaywall: usesPaywall(current),
    // Whether the search behind this list is over. Read off the stamp rather than
    // a boolean beside it: the tab only asks the yes-or-no, but a list that closed
    // has a moment it closed at, and two fields for one fact is how they come to
    // disagree.
    isSecured: current.securedAt !== undefined,
    // Whether there is anything to send. Publish and send is disabled on an empty
    // list: providers are added from Care search, so an empty one is a list
    // somebody started and has not filled yet.
    sendable: current.status === 'draft' && providers.length > 0,
    // Rows messaged with nothing back. Resend is disabled at zero, because it is a
    // nudge rather than the send — there is nobody to chase on a list where
    // everyone has answered.
    awaitingReply: providers.filter(isAwaitingReply).length,
    paidCount: providers.filter((p) => p.state === 'paid').length,
    openProvider: providers.find((p) => p.id === openRow) ?? null,

    sendDraft,
    isPublishOpen: sendDraft !== null && !previewing,
    isHomePublishOpen: confirmingHomePublish,
    isPreviewOpen: sendDraft !== null && previewing,

    select: setSelectedId,
    openRowDrawer: setOpenRow,
    closeRowDrawer: () => setOpenRow(null),

    dialog,
    openCreate: () => setDialog('create'),
    openEdit: () => setDialog('edit'),
    closeDialog: () => setDialog(null),
    // What a create opens on. The lists are numbered, so the next one is the
    // count plus one — a CAM naming a list the same way the last three were named
    // should not have to type it.
    nextListName: `Shortlist ${lists.length + 1}`,
    // A new list is a name and nothing else, because that is all the shared
    // modal asks for: what care it is for and where come off the search that
    // fills it, so they are inherited from the list already open rather than
    // asked for twice.
    //
    // It starts empty and selected. Empty because providers are added from a care
    // search, and selected because a CAM who just made a list is looking for it —
    // leaving them on the old one would read as the click having failed.
    //
    // Prepended: the selector is newest first.
    createList: (name: string) => {
      const id = `sl-new-${lists.length + 1}`;
      setLists((prev) => [
        {
          id,
          name,
          careType: current.careType,
          location: current.location,
          careRequest: current.careRequest,
          status: 'draft' as const,
          providers: [],
        },
        ...prev,
      ]);
      setSelectedId(id);
      setDialog(null);
    },
    // Editing is a rename. Not the status and not the providers: a rename is not a
    // republish, and a published list stays published with the family still
    // looking at it.
    renameList: (name: string) => {
      setLists((prev) =>
        prev.map((l) => (l.id === current.id ? { ...l, name } : l)),
      );
      setDialog(null);
    },

    sendLink: (id: string) =>
      patchProvider(id, {
        state: 'messaged',
        sentAt: nextTime(`${current.id}-sent`, NEXT_TIMES),
      }),

    // The follow-up, and the action a CAM uses most. Same email, same link, to
    // everyone messaged with nothing back. One click: if chasing costs anything
    // they will go back to the phone.
    resendAll: () =>
      setLists((prev) =>
        prev.map((l) =>
          l.id !== current.id
            ? l
            : {
                ...l,
                providers: l.providers.map((p) =>
                  isAwaitingReply(p)
                    ? {
                        ...p,
                        sentAt: nextTime(`${current.id}-sent`, NEXT_TIMES),
                      }
                    : p,
                ),
              },
        ),
      ),
    // Publishing a care home list, which is the same word doing a much smaller
    // job: it makes the list live to the family and writes to nobody.
    //
    // It does go behind a confirm, though nothing is sent. The dom modal exists
    // so a CAM reads the words before they leave; this one exists because of what
    // the click does at the other end — every home is in front of the family the
    // moment it lands, with no payment gate to hold any of them back. A CAM who
    // spends the day on dom lists carries the opposite rule over, so the one
    // sentence is worth the click.
    //
    // Rows do not move either. A dom row goes to messaged because publishing
    // emailed it; a care home row already says where the call got to, and
    // publishing is not news about the call.
    openHomePublish: () => setConfirmingHomePublish(true),
    cancelHomePublish: () => setConfirmingHomePublish(false),
    publishWithoutSending: () => {
      setConfirmingHomePublish(false);
      setLists((prev) =>
        prev.map((l) =>
          l.id !== current.id
            ? l
            : { ...l, status: 'published' as const, publishedAt: PUBLISHED_AT },
        ),
      );
    },
    // The end of the search, and the one thing on this tab a CAM knows that the
    // system cannot work out: the family arranged care and told them on a call.
    // It leaves the list published, the rows where they are and the family's copy
    // exactly as it was — nothing here is a correction, it is a note that the
    // reason for chasing has gone.
    //
    // No confirm, same as Make visible: the CAM is looking at the list they are
    // deciding about, and the click is the decision.
    markSecured: () =>
      setLists((prev) =>
        prev.map((l) =>
          l.id !== current.id ? l : { ...l, securedAt: SECURED_AT },
        ),
      ),
    confirmPayment: (id: string) =>
      patchProvider(id, {
        state: 'paid',
        paidAt: nextTime(`${current.id}-paid`, PAID_TIMES),
      }),
    // The override, on one row. A provider who has not paid, put in front of the
    // family anyway because a CAM rates them and the family needs a shortlist. No
    // confirm: there is one row, the CAM is looking at it, and the click is the
    // decision. Paying does this on its own, which is why this is only ever
    // reached from an unpaid row.
    makeVisible: (id: string) => patchProvider(id, { published: true }),
    // No setter for the dom status. It is read-only on the row and in the drawer,
    // because nothing a CAM does moves it directly — the send, Stripe and the
    // provider's answer do.
    //
    // A care home status is the opposite and always was: a CAM rang the home, so
    // the CAM is the only thing that can move it. It was read-only here only
    // because Care search owned the control, which meant leaving the list to
    // record what a call just said. Same four values, same menu, now on both.
    setContactStatus: (id: string, contactState: ContactStatus) =>
      patchProvider(id, { contactState }),
    // Dismissing from the tab drops the home off this list. No reason dialog
    // behind it, unlike Care search: the reason there feeds the dismissed-card
    // record on the provider, and this tab has no such surface to show it on. If
    // that becomes the way homes get dismissed, the reason should follow.
    dismissProvider: (id: string) =>
      setLists((prev) =>
        prev.map((l) =>
          l.id !== current.id
            ? l
            : { ...l, providers: l.providers.filter((p) => p.id !== id) },
        ),
      ),
    setNote: (id: string, note: string) => patchProvider(id, { note }),

    // What a provider said on the phone, typed up by the CAM who took the call.
    // Stamped here rather than at the call site so every entry carries the stamp
    // whether the form remembers to send one or not: the stamp is the point of
    // the feature, since a transcription nobody owns is worth less than nothing.
    //
    // `you` is this session's CAM, matching how the control panel names the
    // current user elsewhere. It does not move the provider's state: answering a
    // CAM on the phone is not paying, so nothing is released.
    setCamAnswers: (
      id: string,
      answers: Omit<ProviderAnswers, 'source' | 'enteredBy' | 'enteredAt'>,
    ) =>
      patchProvider(id, {
        answers: {
          ...answers,
          source: 'cam',
          enteredBy: 'you',
          enteredAt: CAM_ENTRY_AT,
        },
      }),
    // Publish is two steps because it is the outbound step now: it writes to every
    // provider on the list, so the words go past a CAM first. Opening the modal is
    // the first.
    openPublish: () => {
      setSendDraft(draftFromList(current));
      setPreviewing(false);
    },
    cancelPublish: () => {
      setSendDraft(null);
      setPreviewing(false);
    },
    patchSendDraft: (patch: Partial<SendDraft>) =>
      setSendDraft((prev) => (prev ? { ...prev, ...patch } : prev)),
    openPreview: () => setPreviewing(true),
    closePreview: () => setPreviewing(false),

    // The second, and both halves of it: the list goes live to the family and the
    // links go out to every provider who has not had one. Message and Publish were
    // two actions and are one, so nothing is sent before a CAM has read what is
    // going.
    //
    // Nothing per provider changes about visibility. Nobody has paid at this
    // moment, so the family's list starts empty and fills as providers pay — the
    // gap the empty-shortlist screen exists for.
    publish: () => {
      setLists((prev) =>
        prev.map((l) =>
          l.id !== current.id
            ? l
            : {
                ...l,
                status: 'published' as const,
                publishedAt: PUBLISHED_AT,
                providers: l.providers.map((p) =>
                  p.state === 'draft'
                    ? {
                        ...p,
                        state: 'messaged' as const,
                        sentAt: MESSAGE_ALL_AT,
                      }
                    : p,
                ),
              },
        ),
      );
      setSendDraft(null);
      setPreviewing(false);
    },
  };
};

export type Shortlists = ReturnType<typeof useShortlists>;
